import React, { useEffect, useRef, useState } from "react";

import TEXTURA_GLOBO from "../../utils/IMG/GLOBO/textura_globo.png";
import TEXTURA_NUVENS from "../../utils/IMG/GLOBO/textura_nuvens.png";
import TEXTURA_TEXTURA from "../../utils/IMG/GLOBO/NormalMap.png";
import ESPACO from "../../utils/IMG/OUTRAS/espaco.png";

import { useTranslation } from "react-i18next";
import Globe from "react-globe.gl";
import { BUTTON } from "../form";
import * as THREE from "three";

import "../../utils/font-roboto.scss";
import "./index.scss";

export const Globo = () => {
  const globeEl = useRef();

  const { t } = useTranslation();

  const autoRotateSpeed = 0.1;
  const opacidadeNuvens = 0.15;

  const [bloquearGlobo, setBloquearGlobo] = useState(false);
  const [paisSelecionado, setPaisSelecionado] = useState(null);
  const [countries, setCountries] = useState({ features: [] });
  const [transitionDuration, setTransitionDuration] = useState(1000);
  const [pointOfView, setPointOfView] = useState({
    lat: 0,
    lng: 0,
  });

  const selecionarPais = (objeto) => {
    const globe = globeEl.current;

    if (!objeto || !objeto?.info_tratada) {
      console.error(
        "Objeto ou informações do país não estão definidos:",
        objeto
      );
      return;
    }

    globe.controls().autoRotateSpeed = 0.0001;

    const siglaPais = objeto?.properties?.ADM0_A3;
    const arrayAuxiliar = countries?.features.map((pais) => {
      return {
        ...pais,
        destacado: pais?.properties?.ADM0_A3 === siglaPais,
      };
    });

    const { lat, lng } = objeto?.info_tratada;

    if (objeto?.properties?.POP_EST / 1000000 < 10) {
      setPointOfView(
        {
          lat,
          lng,
          altitude: 0.5,
        },
        1000
      );
    } else if (objeto?.properties?.POP_EST / 1000000 < 100) {
      setPointOfView(
        {
          lat,
          lng,
          altitude: 1,
        },
        1000
      );
    } else {
      setPointOfView(
        {
          lat,
          lng,
          altitude: 1.5,
        },
        1000
      );
    }

    setCountries({ ...countries, features: arrayAuxiliar });
  };

  const limparSelecaoPais = () => {
    const arrayAuxiliar = countries?.features.map((pais) => {
      return {
        ...pais,
        destacado: false,
      };
    });

    setCountries({ ...countries, features: arrayAuxiliar });

    const globe = globeEl.current;

    globe.pointOfView({ lat: 0, lng: 0, altitude: 2 }, 500);
    globe.controls().autoRotate = true;
    globe.controls().autoRotateSpeed = 0.1;
    setPaisSelecionado(null);
  };

  //CONFIGURAÇÕES DE PAISES
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const response = await fetch("/paises.geojson");

        if (!response.ok) {
          throw new Error("Falha ao carregar o arquivo GeoJSON");
        }

        const countriesData = await response.json();

        const specificCountries = [
          "BRA",
          "USA",
          "CHN",
          "AUT",
          "ISR",
          "SVN",
          "TUR",
        ];

        const paisesAtuados = Array?.isArray(countriesData?.features)
          ? countriesData?.features?.filter((pais) => {
              const siglaPais = pais?.properties?.ADM0_A3;
              return specificCountries.includes(siglaPais);
            })
          : [];

        const paises = [
          {
            lat: -14.235,
            lng: -51.9253,
            name: "Brasil",
            sigla: "BRA",
          },
          {
            lat: 46.1512,
            lng: 14.9955,
            name: "Eslovênia",
            sigla: "SVN",
          },
          {
            lat: 37.0902,
            lng: -95.7129,
            name: "Estados Unidos",
            sigla: "USA",
          },
          {
            lat: 35.8617,
            lng: 104.1954,
            name: "China",
            sigla: "CHN",
          },
          {
            lat: 38.9637,
            lng: 35.2433,
            name: "Turquia",
            sigla: "TUR",
          },
          {
            lat: 31.0461,
            lng: 34.8516,
            name: "Israel",
            sigla: "ISR",
          },
          {
            lat: 47.5162,
            lng: 14.5501,
            name: "Áustria",
            sigla: "AUT",
          },
        ];

        const arrayAuxiliar = paisesAtuados.map((paisAtuado) => {
          const siglaPais = paisAtuado?.properties?.ADM0_A3;

          // Encontra o objeto correspondente em `paises`
          const paisCorrespondente = paises.find((p) => p.sigla === siglaPais);

          // Retorna o objeto combinado com a propriedade `selecionarPais`
          return {
            ...paisAtuado,
            destacado: false,
            info_tratada: paisCorrespondente || {}, // adiciona o objeto correspondente ou um objeto vazio caso não encontre
          };
        });
        setCountries({
          ...countriesData,
          features: arrayAuxiliar,
        });

        setTimeout(() => {
          setTransitionDuration(4000);
        }, 3000);
      } catch (error) {
        console.error("Erro ao carregar os dados:", error);
      }
    };

    carregarDados();
  }, []);

  //CONFIGURAÇOES DE ROTAÇÃO
  useEffect(() => {
    const globe = globeEl.current;
    globe.controls().autoRotate = true;
    globe.controls().autoRotateSpeed = autoRotateSpeed;
  }, [autoRotateSpeed]);

  //CONFIGURAÇOES NUVENS
  useEffect(() => {
    const globe = globeEl.current;
    // Adicionar nuvens ao redor do globo
    const cloudTexture = new THREE.TextureLoader().load(TEXTURA_NUVENS);

    // Configura a textura
    const cloudMaterial = new THREE.MeshLambertMaterial({
      map: cloudTexture,
      transparent: true,
      opacity: opacidadeNuvens,
      depthWrite: false,
      depthTest: true,
    });

    // Criação de uma esfera levemente maior para as nuvens
    const cloudSphere = new THREE.Mesh(
      new THREE.SphereGeometry(104, 75, 75), // tamanho ligeiramente maior que o globo
      cloudMaterial
    );

    // Adiciona a esfera de nuvens à cena
    globe.scene().add(cloudSphere);

    // Definir uma rotação lenta para as nuvens
    (function rotateClouds() {
      // Velocidade de rotação
      cloudSphere.rotation.y += 0.00025;
      requestAnimationFrame(rotateClouds);
    })();
  }, []);

  useEffect(() => {
    if (globeEl.current) {
      globeEl.current.pointOfView(pointOfView, 1000); // Duração de 1 segundo para transição
    }
  }, [pointOfView]);

  useEffect(() => {
    const globe = globeEl.current;
    globe.controls().enablePan = bloquearGlobo;
    globe.controls().enableZoom = bloquearGlobo;
    globe.controls().enableRotate = bloquearGlobo;
  }, [bloquearGlobo]);

  return (
    <div className="container-globo">
      <div className="globo">
        <Globe
          key={"globo"}
          ref={globeEl}
          //Configurações de Textura do globo
          showAtmosphere
          globeImageUrl={TEXTURA_GLOBO}
          bumpImageUrl={TEXTURA_TEXTURA}
          //Fundo do Globo
          backgroundImageUrl={ESPACO}
          backgroundColor="rgba(0,0,0,0)"
          animateIn={true}
          // Define a cor e tamanho da atmosfera ao redor do globo
          atmosphereAltitude={0.2}
          atmosphereColor="rgba(225,225,225,0.25)"
          //Interação de Markers poligonais no globo
          enablePointerInteraction={false}
          polygonsData={countries.features.filter(
            (d) => d && d.properties.ISO_A2 !== "AQ" && d.destacado
          )}
          polygonCapColor={(polygon) =>
            polygon && polygon.destacado
              ? "rgba(136, 143, 80,.5)"
              : "rgba(136, 143, 80, 0.5)"
          }
          polygonSideColor={(polygon) =>
            polygon && polygon.destacado
              ? "rgba(136, 143, 80,.15)"
              : "rgba(136, 143, 80, 0.15)"
          }
          polygonAltitude={(polygon) =>
            polygon && polygon.destacado ? 0.05 : 0.025
          }
          polygonLabel={({ properties: d }) =>
            d
              ? `
        <b>${d.ADMIN} (${d.ISO_A2})</b> <br /> 
      `
              : ""
          }
          polygonsTransitionDuration={transitionDuration}
        />
      </div>
      <div
        className={`${!bloquearGlobo ? "tela-bloqueada" : " container-paises"}`}
      >
        {countries?.features?.length !== 0 && (
          <>
            {bloquearGlobo ? (
              <>
                <div className="titulo px24">{t("onde_atuamos.paises")}</div>
                <div className="lista-paises light px18">
                  {countries?.features
                    ?.sort((a, b) =>
                      a.info_tratada.name.localeCompare(b.info_tratada.name)
                    )
                    ?.map((pais) => {
                      return (
                        <div className="item">
                          <p
                            className={`nome ${
                              paisSelecionado?.name ===
                                pais?.info_tratada?.name && "selecionado px24"
                            }`}
                            style={{}}
                            key={pais?.info_tratada?.name}
                            onClick={() => {
                              selecionarPais(pais);
                              setPaisSelecionado(pais?.info_tratada);
                            }}
                          >
                            {`${pais?.info_tratada?.name} (${pais?.info_tratada?.sigla})`}
                          </p>
                        </div>
                      );
                    })}
                </div>
                <div className="botoes">
                  <div
                    style={{
                      filter: `${
                        paisSelecionado ? "opacity(1)" : "opacity(0)"
                      }`,
                    }}
                  >
                    <BUTTON
                      placeholder={t("onde_atuamos.limpar")}
                      onClick={() => paisSelecionado && limparSelecaoPais()}
                    />
                  </div>
                  <BUTTON
                    placeholder={t("onde_atuamos.voltar")}
                    onClick={() => setBloquearGlobo(false)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="texto px36">
                  {t("onde_atuamos.brw_pelo_mundo")}
                </div>
                <BUTTON
                  placeholder={t("onde_atuamos.botao")}
                  onClick={() => setBloquearGlobo(true)}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
